import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import AddIcon from 'ui-library/icons/AddIcon';
import {isJSONArray, JSONObject, JSONValue} from 'util/Types';

import jsonSpecToInitialValue from '../initial-values/jsonSpecToInitialValue';
import {
  doesConditionMatch,
  getConfigShapeAtConfigShapePath,
  maybeGetValueFromConfig,
} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  BANNER_KEY,
  EXPANDABLE_DETAILS_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  INFO_TEXT_UI_SCHEMA_KEY,
  IS_COLLAPSIBLE_SCHEMA_KEY,
  isArrayFieldSettings,
  isFieldUISchemaWithStandardComponent,
  isShapeUISchemaFieldComponentValue,
  isShapeUISchemaNestedShapeValues,
  NUM_COLUMNS_KEY,
  SECTION_ADD_ICON_FIELD_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchema,
  ConfigShapePath,
  SHOW_DIVIDER_LINE_KEY,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  FieldUISchemaBanner,
} from '../StepConfigEditorTypes';
import {getSectionNameOrNull} from './SectionName';
import StepConfigEditorBanner from './StepConfigEditorBanner';
import StepConfigEditorRouter from './StepConfigEditorRouter';
import StepConfigFieldsTemplate, {CollapsingSection} from './StepConfigFieldsTemplate';

export interface ShapeEditorProps {
  config: JSONObject;
  configShape: PipelineConfigJsonSpec;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  uiSchema: ShapeUISchema;
}

/** @package */
export default function ShapeEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
  uiSchema,
}: ShapeEditorProps): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();

  function handleChangeConfig(config: JSONObject, path: ConfigShapePath, newValue: JSONValue): void {
    if (uiSchema?.[ON_CHANGE_CONFIG_MIDDLEWARE_KEY]) {
      onChangeConfig(
        uiSchema[ON_CHANGE_CONFIG_MIDDLEWARE_KEY](newValue, path, config, dataFramesInfos),
        path,
        newValue
      );
    } else {
      onChangeConfig(config, path, newValue);
    }
  }

  function shouldDisplayBanner(banner: FieldUISchemaBanner): boolean {
    if (banner.visibilityFn == null) {
      return true; // If no visibility function, always show
    }
    return banner.visibilityFn(config);
  }

  const fieldOrder = uiSchema[FIELD_ORDER_UI_SCHEMA_KEY];
  const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];
  const sectionInfoText = uiSchema?.[INFO_TEXT_UI_SCHEMA_KEY];
  const isCollapsible = uiSchema?.[IS_COLLAPSIBLE_SCHEMA_KEY];
  const numColumns = uiSchema?.[NUM_COLUMNS_KEY];

  if (fieldOrder.length === 0) {
    return (
      <InlineBanner bannerType={InlineBannerType.INFO} text="This step does not require configuration." />
    );
  }

  if (isShapeUISchemaNestedShapeValues(uiSchema)) {
    assert(numColumns == null, 'Num columns not supported with nested shapes');
    const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];
    const sectionInfoText = uiSchema?.[INFO_TEXT_UI_SCHEMA_KEY];
    const sectionNameOrNull = getSectionNameOrNull({sectionName, sectionInfoText});

    const fieldElements = fieldOrder.map((variableName) => (
      <StepConfigEditorRouter
        key={variableName}
        config={config}
        configShape={configShape}
        dataTestAttrs={{key: variableName}}
        onChangeConfig={handleChangeConfig}
        disabled={disabled}
        uiSchema={uiSchema[variableName]}
      />
    ));

    if (isCollapsible) {
      return <CollapsingSection sectionHeader={sectionNameOrNull}>{fieldElements}</CollapsingSection>;
    }

    const banner = uiSchema[BANNER_KEY];
    const showBanner = banner && shouldDisplayBanner(banner);
    const bannerPlacement = banner?.placement ?? 'above';

    return (
      <>
        {showBanner && bannerPlacement === 'above' && <StepConfigEditorBanner {...banner} />}
        {sectionNameOrNull}
        {showBanner && bannerPlacement === 'below' && <StepConfigEditorBanner {...banner} />}
        {fieldElements}
      </>
    );
  }
  assert(isShapeUISchemaFieldComponentValue(uiSchema), 'uiSchema must be a ShapeUISchemaFieldComponentValue');
  const expandableDetails = uiSchema?.[EXPANDABLE_DETAILS_KEY];
  const showExpandToggle =
    expandableDetails?.visibleWhen == null || doesConditionMatch(config, expandableDetails.visibleWhen);

  const addIconFieldName = uiSchema[SECTION_ADD_ICON_FIELD_KEY];
  const sectionAddIcon = buildArrayAddIcon(uiSchema, configShape, config, handleChangeConfig, disabled);

  const fields = fieldOrder.map((variableName) => {
    const field = uiSchema[variableName];
    const isExpandable = showExpandToggle && variableName === expandableDetails?.headerField;
    assert(field != null, `Field ${variableName} is not defined in uiSchema`);

    const currentConfigShape = isFieldUISchemaWithStandardComponent(field)
      ? getConfigShapeAtConfigShapePath(field.configShapePath, configShape, config)
      : undefined;

    // Extract banner from field if it exists and should be displayed
    const fieldBanner =
      isFieldUISchemaWithStandardComponent(field) &&
      field[BANNER_KEY] &&
      shouldDisplayBanner(field[BANNER_KEY]) ? (
        <StepConfigEditorBanner {...field[BANNER_KEY]} />
      ) : undefined;

    // Auto-suppress the default add button for the field whose add icon is rendered inline in the
    // section header (see SECTION_ADD_ICON_FIELD_KEY).
    const fieldSchema =
      variableName === addIconFieldName && isFieldUISchemaWithStandardComponent(field)
        ? {
            ...field,
            fieldSettings:
              field.fieldSettings != null && isArrayFieldSettings(field.fieldSettings)
                ? {...field.fieldSettings, hideDefaultAddButton: true}
                : {hideDefaultAddButton: true},
          }
        : field;

    return {
      systemVariableName: variableName,
      // eslint-disable-next-line benchlint/no-in-operator
      variableName: 'displayName' in field ? field.displayName : undefined,
      // eslint-disable-next-line benchlint/no-in-operator
      infoText: 'infoText' in field ? field.infoText : undefined,
      required: currentConfigShape?.required,
      banner: fieldBanner,
      editor: (
        <StepConfigEditorRouter
          key={variableName}
          config={config}
          configShape={configShape}
          dataTestAttrs={{key: variableName}}
          onChangeConfig={handleChangeConfig}
          disabled={disabled}
          uiSchema={fieldSchema}
          isExpandable={isExpandable}
        />
      ),
    };
  });

  const banner = uiSchema[BANNER_KEY];
  const showBanner = banner && shouldDisplayBanner(banner);
  const bannerPlacement = banner?.placement ?? 'above';

  return (
    <>
      {showBanner && bannerPlacement === 'above' && <StepConfigEditorBanner {...banner} />}
      <StepConfigFieldsTemplate
        numColumns={numColumns ?? 1}
        fields={fields}
        sectionName={sectionName}
        isCollapsible={isCollapsible}
        sectionInfoText={sectionInfoText}
        isCompact={true}
        expandableDetails={expandableDetails}
        sectionAddIcon={sectionAddIcon}
        banner={
          showBanner && bannerPlacement === 'below' ? <StepConfigEditorBanner {...banner} /> : undefined
        }
      />
      {uiSchema[SHOW_DIVIDER_LINE_KEY] && <hr className={css(styles.dividerLine)} />}
    </>
  );
}

function buildArrayAddIcon(
  uiSchema: ShapeUISchema,
  configShape: PipelineConfigJsonSpec,
  config: JSONObject,
  handleChangeConfig: (config: JSONObject, path: ConfigShapePath, newValue: JSONValue) => void,
  disabled: boolean | undefined
): JSX.Element | undefined {
  const addIconFieldName = uiSchema[SECTION_ADD_ICON_FIELD_KEY];
  const fieldSchema = addIconFieldName != null ? uiSchema[addIconFieldName] : undefined;
  if (fieldSchema == null || !isFieldUISchemaWithStandardComponent(fieldSchema)) {
    return undefined;
  }

  const configPath = fieldSchema.configShapePath;
  const fieldConfigShape = getConfigShapeAtConfigShapePath(configPath, configShape, config);
  if (fieldConfigShape?.kind !== 'array') {
    return undefined;
  }

  const disableAddRemove =
    fieldSchema.fieldSettings != null &&
    isArrayFieldSettings(fieldSchema.fieldSettings) &&
    fieldSchema.fieldSettings.disableAddRemove === true;
  // Mirror ArrayEditor, which omits the add button when the editor is disabled or add/remove is disabled.
  if (disabled || disableAddRemove) {
    return undefined;
  }

  const arrayValue = maybeGetValueFromConfig(config, configPath) ?? [];
  const isAddDisabled =
    fieldConfigShape.maxItems != null &&
    isJSONArray(arrayValue) &&
    arrayValue.length >= fieldConfigShape.maxItems;
  const elemUISchema = fieldSchema.elementUISchema;

  return (
    <Tooltip
      isDisabled={!isAddDisabled}
      text={fieldConfigShape.maxItems != null ? `Cannot exceed ${fieldConfigShape.maxItems} items` : ''}
    >
      <AddIcon
        disabled={isAddDisabled}
        onClick={() => {
          const arr = isJSONArray(arrayValue) ? arrayValue : [];
          const newElement = jsonSpecToInitialValue(fieldConfigShape.elementSpec, elemUISchema);
          handleChangeConfig(config, configPath, [...arr, newElement]);
        }}
      />
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  dividerLine: {
    marginTop: 4,
    marginBottom: 10,
    borderTop: `1px solid ${GRAY_LIGHT}`,
    width: '100%',
  },
});
